import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Compass, PawPrint, ShoppingBag, Smile, User, WandSparkles } from 'lucide-react';

import { BackgammonSectionTabs, type SchedaDef } from '@/components/backgammon/BackgammonSectionTabs';
import { RichiedeAccesso } from '@/components/common/RichiedeAccesso';
import { useGetFeaturedAvatarQuery, useGetShopOwnedQuery } from '@/services/avatarApi';
import { usePuoCreareAvatar } from '@/hooks/usePuoCreareAvatar';
import { useOspite } from '@/hooks/useOspite';
import { env } from '@/env';
import { ROTTA_SCHEDA, schedePerRiga, schedeVisibili, type SchedaAvatar } from '@/lib/avatarSchede';

/**
 * LA SEZIONE AVATAR A SCHEDE (I-635/I-636, variante A di Luca; condizioni A1-A14 di tma-orcb). Solo col flag `VITE_FEATURE_AVATAR_SCHEDE_ENABLED`
 * (solo staging): la testata con l'avatar in uso e la riga di schede stanno qui, il contenuto della scheda e' `children`. Ogni scheda ha la SUA
 * rotta (`ROTTA_SCHEDA`), cosi' il tasto Indietro e i link diretti continuano a funzionare; il cambio di scheda e' un `replace` (Indietro esce dalla sezione).
 *
 * ⚠️ Le schede dicono cosa SI VEDE, non cosa si PUO' aprire: le rotte e i loro cancelli (flag di #685 e #691, `CreatoreGate` di #694) sono del router.
 * «Emote» e «Negozio» ci sono solo coi loro DUE flag (`env` + confronto LETTERALE, come in `Avatars.tsx`), «Editor» solo con `usePuoCreareAvatar()`.
 * ⛔ Il marcatore `data-avatar-schede` sta SOLO qui: questo file e' dietro il `lazy()` col confronto letterale, quindi nella build di produzione non c'e'.
 */
const ICONE: Record<SchedaAvatar, SchedaDef['Icon']> = {
  mine: User,
  explore: Compass,
  emotes: Smile,
  shop: ShoppingBag,
  studio: WandSparkles,
};

/**
 * Quante schede stanno AL MASSIMO in una riga. CINQUE e' la DEROGA alla regola «righe da quattro» della Hub (A9 di tma-orcb) e vale SOLO se misurata:
 * a 360×640 e 390×844, in it/en/es/el, `scrollWidth ≤ clientWidth` su ogni etichetta e tocco ≥ 44 px (`tools/banco-pixel/avatar-schede.mjs`).
 * Oltre le cinque (6 con i Qlings e il creatore) le righe sono BILANCIATE: `schedePerRiga` (3 + 3, non 5 + 1). Il calcolo sta qui e non in `BackgammonSectionTabs`, che e' condiviso.
 */
export const SCHEDE_PER_RIGA = 5;

/** La scheda «Qpets» (I-699, prototipo, solo staging): sta FUORI da `SchedaAvatar`, cosi' con il suo flag spento elenco, ordine e rotte della sezione non cambiano. */
export const ROTTA_QPETS = '/avatars/qpets';

export function AvatarSezione({ scheda, children }: { scheda: SchedaAvatar | 'qpets'; children: ReactNode }) {
  const { t } = useTranslation('avatars');
  const navigate = useNavigate();
  const puoCreare = usePuoCreareAvatar();
  const ospite = useOspite();
  const negozio = env.featureAvatarShopEnabled && import.meta.env.VITE_FEATURE_AVATAR_SHOP_ENABLED === 'true';
  const chiavi = schedeVisibili({
    emote: env.featureAvatarEmotesEnabled && import.meta.env.VITE_FEATURE_AVATAR_EMOTES_ENABLED === 'true',
    negozio,
    editor: puoCreare,
  });
  const sections: SchedaDef<SchedaAvatar | 'qpets'>[] = chiavi.map((key) => ({
    key,
    Icon: ICONE[key],
    label: t(`sezione.schede.${key}`),
  }));
  // Due letture del flag dei Qpets (`env` per i test, il confronto LETTERALE per il bundler); ultima scheda, il nome in UNA chiave sola.
  if (env.featureQpetsEnabled && import.meta.env.VITE_FEATURE_QPETS_ENABLED === 'true') sections.push({ key: 'qpets', Icon: PawPrint, label: t('qpets.nome') });
  // La testata c'e' dove serve l'avatar in uso (le tavole A1, A3, A5): non in «Esplora» (la galleria degli altri) e non in «Negozio», che
  // legge gia' la vetrina per dire «In vetrina» sulle sue righe.
  const conTestata = scheda === 'mine' || scheda === 'emotes' || scheda === 'studio';

  return (
    <div data-avatar-schede data-scheda={scheda}>
      <div className="flex flex-col gap-3 px-4 pt-5">
        {conTestata && (ospite ? <RichiedeAccesso forma="sezione" /> : <TestataAvatar negozio={negozio} />)}
        <BackgammonSectionTabs
          sections={sections}
          active={scheda}
          onSelect={(key) => navigate(key === 'qpets' ? ROTTA_QPETS : ROTTA_SCHEDA[key], { replace: true })}
          ariaLabel={t('sezione.aria')}
          testIdPrefix="avatar-scheda"
          perRiga={schedePerRiga(sections.length, SCHEDE_PER_RIGA)}
        />
      </div>
      {children}
    </div>
  );
}

/**
 * La testata: l'avatar in uso, da `GET /avatars/featured` (`useGetFeaturedAvatarQuery`, A10): nessun endpoint e nessun parser nuovi. Si legge
 * solo cio' che serve a disegnarla (titolo e miniatura), campo per campo e senza fidarsi del tipo: con un ARTICOLO del negozio in vetrina la
 * forma vera e' `{ _id, item: { itemId }, … }` senza `kind` (misurata dal vivo nel collaudo di #691), e di titolo e immagine puo' non esserci
 * niente. Tre stati distinti, mai un vuoto affermato su una domanda che non ha risposto: in lettura (muta), non leggibile (errore senza dati),
 * nessuno (risposta `null`).
 * L'ospite non monta questo componente (la chiamata sarebbe bloccata da `guardiaOspite`): `AvatarSezione` gli da' «accedi».
 *
 * 🔴 Un ARTICOLO del negozio in vetrina (`{ _id, item: { itemId } }`) non porta ne' titolo ne' immagine: li' si prendono dalla lista dei POSSEDUTI
 * (`GET /avatars/shop/owned`, la stessa che legge il Negozio), per `itemId`. Solo se serve (articolo senza immagine) e solo col negozio acceso (`negozio`, le due
 * letture del flag fatte da `AvatarSezione`): negli altri casi nessuna chiamata in piu'. Se l'articolo non e' nella prima pagina dei posseduti, o la lettura fallisce,
 * resta com'era: niente miniatura, «Avatar in vetrina» — mai un nome o un'immagine inventati.
 */
function TestataAvatar({ negozio }: { negozio: boolean }) {
  const { t } = useTranslation('avatars');
  const { data, isLoading, isError } = useGetFeaturedAvatarQuery();
  const stato = data ? 'avatar' : isLoading ? 'carico' : isError ? 'errore' : 'nessuno';
  const titoloProprio = typeof data?.title === 'string' && data.title.trim() !== '' ? data.title : null;
  const miniaturaPropria = [data?.thumbnailUrl, data?.imageUrl].find((u): u is string => typeof u === 'string' && u !== '') ?? null;
  const itemId = (data as { item?: { itemId?: unknown } } | null | undefined)?.item?.itemId;
  const cercaArticolo = negozio && stato === 'avatar' && miniaturaPropria === null && typeof itemId === 'string' && itemId !== '';
  const posseduti = useGetShopOwnedQuery(undefined, { skip: !cercaArticolo });
  const voce = cercaArticolo ? posseduti.data?.items.find((v) => v.itemId === itemId) : undefined;
  const titolo = titoloProprio ?? voce?.title ?? null;
  const miniatura = miniaturaPropria ?? voce?.thumbnail ?? voce?.image ?? null;

  return (
    <div
      data-testid="avatar-testata"
      data-stato={stato}
      className="card-edge flex min-h-24 shrink-0 items-center gap-3 rounded-2xl bg-bg/55 p-3 backdrop-blur-md"
    >
      <div className="h-[72px] w-[72px] shrink-0 overflow-hidden rounded-xl bg-bg-secondary">
        {miniatura && <img src={miniatura} alt="" className="h-full w-full object-contain" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-wide text-fg-muted">{t('sezione.testata.label')}</p>
        {stato === 'avatar' && (
          <>
            <p className="truncate font-display text-base font-bold leading-tight text-fg">{titolo ?? t('sezione.testata.inVetrina')}</p>
            <span className="mt-1.5 inline-block rounded-full bg-brand/15 px-2 py-0.5 text-[11px] font-semibold text-brand">
              {t('sezione.testata.inUso')}
            </span>
          </>
        )}
        {stato === 'nessuno' && <p className="mt-0.5 text-sm text-fg-muted">{t('sezione.testata.nessuno')}</p>}
        {stato === 'errore' && <p className="mt-0.5 text-sm text-danger">{t('sezione.testata.nonLeggibile')}</p>}
      </div>
    </div>
  );
}
